import type { Loan } from './loan';
import type { Address } from './address';
import type { Category, ServiceCategory } from './category';
import type { Group } from './group';
import type { User } from 'app/types/user';
import type { Profile } from 'app/types/profile';
import type { ResponseWithSignals } from 'app/network/signals';
import type { ImagePickerAsset, ImagePickerSuccessResult } from 'expo-image-picker';
import type { Tag } from 'app/network/listing';
import type { PublicLocation } from './location';
export type AcceptedLoan = Pick<Loan, 'start' | 'end'> & Pick<Partial<Loan>, 'id' | 'borrower'> & { distance?: number };

export interface ListingPicture {
  id: string;
  url: string;
  position: number;
  createdAt: string;
  postId?: string;
}

export interface ListingBorrower {
  id: string;
  firstName: string;
  lastName: string;
  picture: string | null;
  isSuper: boolean;
  isAmbassador: boolean;
  lastBorrowedAt: string;
  borrowCount: number;
  isCurrent: boolean;
  currentLoanEnd: string | null;
}

export interface ListingsTag {
  listingId: string;
  tagId: string;
  tag: Tag;
  tagName: string;
  score: number | null;
}
export interface NeedMatch {
  status: ListingMatchStatus;
  candidateUser?: { id: User['id']; profile: { picture: string | null } } | null;
}

export enum ListingMatchStatus {
  PENDING = 'PENDING', // Not yet messaged
  INITIATED = 'INITIATED', // The candidate messaged the borrower before the concierge.
  SENT = 'SENT', // Message sent to candidate, or candidate messaged the borrower about the need
  MATCH = 'MATCH', // There is an accepted date proposal between the borrower and the candidate -> Request marked as found
  ACCEPTED = 'ACCEPTED', // Candidate accepted to help, but no date proposal yet
  REJECTED = 'REJECTED', // Candidate rejected to help.
  EXPIRED = 'EXPIRED', // No response within the time limit
  RESOLVED = 'RESOLVED', // A match was found with another candidate OR a date proposal was accepted without the concierge.
  ABORTED = 'ABORTED', // If a listing becomes unavailable (archived, expired, deleted, ...) we want to mark all pending and initiated matches as aborted.
}
export enum TransactionMode {
  sell = 'sell',
  lend = 'lend',
  give = 'give',
}

interface ListingBase {
  id: string;
  addresses?: Pick<Address, 'id'>[];
  categoryPrimary: Category;
  createdAt: string;
  description: string | null;
  favourites?: [Pick<Listing, 'id'> & { createdAt: string }];
  groups?: Pick<Group, 'id' | 'name' | 'picture'>[];
  item: {
    id: string;
    name: string;
  };
  status: 'archived' | 'available' | 'deleted' | 'expired' | 'loaned' | 'unavailable' | 'was_loaned'; // was_loaned is a virtual status, used for requests that have been found
  updatedAt: string;
  user: User & { profile: Profile };
  userId: string;
  grouping?: ListingGrouping;
  estimatedPrice?: number | null;
  transactionMode?: TransactionMode | null;
  customDistance?: number | null;
  customCenterLatitude?: number | null;
  customCenterLongitude?: number | null;
  distance?: number;
  distanceFrom?: {
    name?: string;
    icon?: number;
  };
  publicLocation?: PublicLocation;
  publicLocations?: PublicLocation[];
  numViews?: number;
  numInteractions?: number;
  numFavourites?: number;
  numComments?: number;
  numLoans?: number;
  /**
   * List of lenders who contact the borrower to help them with their request
   */
  lenders?: { id: string; picture?: string }[];
  /**
   * Accepted loan for this listings
   */
  lender?: {
    acceptedAt: string;
    id: string;
    name: string;
    picture?: string;
    hasAcceptedLoan?: boolean;
    city?: string;
    distance?: number;
    lastMessageDate?: string;
  } | null;
  additionalLendersCount?: number;
  // This is the list of accepted loans for this listing - used to display availability of offers
  // borrower property will only be present for the user's own listings
  loans?: AcceptedLoan[];
  quantity?: number | null;
  picture: string | null;
  pictures?: ListingPicture[];
  subtype: ListingSubtype;
  serviceCategory?: ServiceCategory;
  before?: string | null;
  loanFee?: number | null;
  loanFeeDescription?: string | null;
  loanFeePaymentMethod?: string | null;
  timeStart?: string | null;
  timeEnd?: string | null;
  address?: Pick<Address, 'street' | 'unit' | 'city' | 'administrativeArea' | 'postalCode' | 'country'> | null;
  isCurrentlyUnavailable?: boolean;
  maxParticipants?: number | null;
  /** User-entered price (e.g. "50 $/kg", "100€"). Required when transactionMode === 'sell'. */
  price?: string | null;
  /** Optional free-form inventory (e.g. "1 kg/lb/uté"). */
  inventory?: string | null;
  /** Auto-true when subtype === 'donation'. */
  isDonation?: boolean;
  /**
   * Only allowed when `subtype === 'expirable'`. When set, the listing is highlighted
   * in the UI and the API sends an instant in-app + push notification to members who
   * can see the listing.
   */
  urgent?: boolean;
  numParticipants?: number | null;
  myParticipants?: number | null;
  listingsTags?: ListingsTag[];
  canBonify?: boolean;
  needMatches?: NeedMatch[];
  conciergerieOff?: boolean;
  conciergerieDisabledByUser?: boolean;
  dismissed?: boolean;
}

export interface PartialListingGrouping {
  id: string;
  key: string;
  totalOffers: number;
  groupingFavourites?: [Pick<ListingGrouping, 'id'>];
  listings: (Pick<Listing, 'id' | 'distance' | 'picture' | 'favourites'> & {
    user: Pick<Listing['user'], 'id' | 'isSuper' | 'isAmbassador' | 'trustPoints' | 'lastSeenAt'> & {
      profile: Pick<Listing['user']['profile'], 'picture' | 'firstName' | 'lastName'>;
    };
    groups?: { id: string; name: string }[];
  })[];
}

class GroupingFavourite {
  id!: string;
  groupingId!: string;
  userId!: string;
  createdAt!: string;
}

export interface ListingGrouping {
  id: string;
  key: string;
  totalOffers: number;
  listings: Listing[];
  groupingFavourites?: GroupingFavourite[];
}

export enum ListingType {
  offer = 'offer',
  request = 'request',
}

export enum ListingSubtype {
  object = 'object',
  service = 'service',
  donation = 'donation',
  expirable = 'expirable',
}

export interface Offer extends ListingBase {
  type: ListingType.offer;
  start: string | null;
  end: string | null;
}

export interface Request extends ListingBase {
  type: ListingType.request;
  start: string | null;
  end: string | null;
  automaticMessage?: string | null;
  contactedMembers?: number;
  numActiveMatches?: number;
  numMatches?: number;
}

export interface Favorite {
  id: string;
  listingId: string;
}

export interface FavouriteGrouping {
  id: string;
  groupingId: string;
}

export type Listing = (Offer | Request) & ResponseWithSignals;

/** Entry point that led to creation. The API tracks the final creation method separately. */
export type NewListingSource =
  | '+'
  | 'tutorial'
  | 'onboardingV2'
  | 'inline_banner_5'
  | 'inline_banner_20'
  | 'trial_offer'
  | 'babillard_empty'
  | 'quick_offer_banner'
  | 'babillard_after_search_results'
  | 'intro'
  | 'needs_list_top_banner'
  | 'offers_list_top_banner'
  | 'landing_page_growth_promo'
  | 'landing_page_immature_neighbourhood'
  | 'help'
  | 'free_subscription'
  | 'streak_contribution'
  | 'streak_tip';
export interface CreateListingDTO extends Pick<
  Listing,
  | 'description'
  | 'end'
  | 'start'
  | 'type'
  | 'subtype'
  | 'customDistance'
  | 'customCenterLatitude'
  | 'customCenterLongitude'
  | 'quantity'
  | 'estimatedPrice'
  | 'transactionMode'
  | 'loanFee'
  | 'loanFeeDescription'
  | 'loanFeePaymentMethod'
  | 'timeStart'
  | 'timeEnd'
  | 'address'
> {
  clientRequestId?: string;
  popularItemId?: string;
  source?: NewListingSource;
  item: Pick<Listing['item'], 'name'>;
  addressIds?: Address['id'][];
  categoryPrimaryId: Category['id'] | null;
  serviceCategoryId: ServiceCategory['id'] | null;
  groupIds?: Group['id'][];
  picture: ImagePickerAsset | ImagePickerSuccessResult | null;
  pictures?: ImagePickerAsset[];
  pictureUrl?: string | null;
  blockedDates?: string[];
  maxParticipants?: string | null;
  automaticMessage?: string | null;
  price?: string | null;
  inventory?: string | null;
  urgent?: boolean;
  duplicates?: number | null;
}

export interface CreateListingsDTO {
  listings: (CreateListingDTO & { itemName?: string; source?: NewListingSource })[];
}

export interface UpdateListingDTO {
  addressIds?: Address['id'][];
  categoryPrimaryId?: Category['id'];
  serviceCategoryId?: string | null;
  description?: string | null;
  groupIds?: Group['id'][];
  item?: Listing['item'];
  subtype?: Listing['subtype'];
  status?: Listing['status'];
  end?: Listing['end'];
  start?: Listing['start'];
  customDistance?: number | null;
  customCenterLatitude?: number | null;
  customCenterLongitude?: number | null;
  quantity?: number | null;
  estimatedPrice?: number | null;
  transactionMode?: TransactionMode | null;
  before?: Listing['before'];
  loanFee?: number | null;
  loanFeeDescription?: string | null;
  loanFeePaymentMethod?: string | null;
  timeStart?: string | null;
  timeEnd?: string | null;
  address?: Listing['address'] | null;
  maxParticipants?: string | null;
  automaticMessage?: string | null;
  price?: string | null;
  inventory?: string | null;
  urgent?: boolean;
  conciergerieDisabledByUser?: boolean;
  duplicates?: number | null;
}

export interface CreateFavouriteDTO {
  listingId: Listing['id'];
}

export interface CreateGroupingFavouriteDTO {
  groupingId: ListingGrouping['id'];
}

export enum ListingsSortBy {
  Date = 'date',
  Distance = 'distance',
  Popularity = 'popularity',
  Random = 'random',
  StartDate = 'startDate',
  Urgency = 'urgency', // alias of StartDate
  Relevance = 'relevance',
}

export interface FindAllListingsDTO {
  category?: Category['id'];
  serviceCategory?: ServiceCategory['id'];
  ids?: Listing['id'][];
  search?: string;
  take?: number;
  type?: Listing['type'];
  tags?: Tag['id'][];
  subtypes?: Listing['subtype'][];
  includeCounts?: boolean;
  includeTrustPoints?: boolean;
  userId?: User['id'];
  sharingPlaceUserId?: User['id']; // used to find listings from a sharing place (not the listings of the sharing place user, but the listings of the members of the sharing place)
  groupIds?: Group['id'][];
  groupIdsFilterActive?: boolean;
  addressIds?: Address['id'][];
  doGrouping?: boolean;
  searchCenterLatitude?: number;
  searchCenterLongitude?: number;
  searchDistance?: number;
  skipOthersRequests?: boolean;
  hideFoundRequests?: boolean;
  dismissedOnly?: boolean;
  hideOffersWithFee?: boolean;
  sortBy?: ListingsSortBy;
  /** Filter offers by transaction mode (lend/sell/give). Omit or undefined for "all". */
  transactionMode?: TransactionMode;
  /**
   * Filter offers by one or more transaction modes (lend/sell/give). When set,
   * the API returns only listings whose transactionMode is in this list.
   * Omit or pass an empty array for "all" (no transaction mode filter).
   * Used by the Haute-Gaspésie white label multi-toggle filter.
   */
  transactionModes?: TransactionMode[];
  __counter?: number;
}

export interface ListingMapViewport {
  north: number;
  south: number;
  east: number;
  west: number;
  zoom: number;
}

export interface FindListingsMapDTO extends FindAllListingsDTO, ListingMapViewport {
  cluster?: 'true' | 'false';
  clusteringVersion?: 2;
  limit?: number;
}

export interface ListingsMapCluster {
  id: string;
  count: number;
  listingIds: Listing['id'][];
  pointIds?: string[];
  latitude: number;
  longitude: number;
  bounds: Pick<ListingMapViewport, 'north' | 'south' | 'east' | 'west'>;
}

export interface ListingsMapItem {
  id?: string;
  listing: Listing;
  publicLocation: PublicLocation;
}

export interface ListingsMapResponse {
  clusters: ListingsMapCluster[];
  listings: ListingsMapItem[];
  total: number;
}

export type CategoryWithOffers = Category & {
  offers: Listing[];
};

export interface ListingsByCategory {
  categories: {
    id: Category['id'];
    name: string;
  }[];
  results: {
    categoryId: Category['id'];
    listings: Listing[];
  }[];
}

export interface ListingComment {
  id: string;
  listingId: string;
  body: string;
  createdAt: string;
  user: {
    id: string;
    profile: {
      firstName: string;
      lastName: string;
      picture?: string | null;
    };
  };
}
